'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useHotelMapModal } from '@/app/components/hotel-detail/HotelMapModalProvider';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { trackViewMap } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useTranslation } from '@/app/components/translations/Translation';
import { buildHotelStaticMapUrl } from '@/app/components/hotel-detail/common/staticMap';
import { getTshPreviewFraming, tshAreaCenter } from '@/utils/mapUtils';
import { Icon } from '@/app/common/icons/Icon';

type EvoLocationMapProps = {
  /** Logical px requested from Mapbox (capped at 1280); only needs to cover the rendered width. */
  width: number;
  /** Rendered map height in px (Figma: 256 desktop, 320 mobile). */
  height: number;
  /** Corner radius in px (Figma: 8 desktop, 16 mobile). */
  borderRadius: number;
  /** Mobile shows the "Open map" control as a full-width bar; desktop as a bottom-right pill. */
  fullWidthButton?: boolean;
};

// Relative wrapper so the attribution chip can overlay the map as a sibling of the button (an <a>
// cannot live inside the <button>).
const MapFigure = styled.div`
  position: relative;
`;

const MapButton = styled.button<{ $radius: number }>`
  position: relative;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  border-radius: ${({ $radius }) => $radius}px;
  overflow: hidden;
  line-height: 0;
`;

const MapImg = styled.img<{ $height: number }>`
  display: block;
  width: 100%;
  height: ${({ $height }) => $height}px;
  object-fit: cover;
`;

// The hotel is dead-centre of the static image, so the marker is pinned there with its tail tip on
// that point (translate(-50%, -100%)). Matches Figma node 979:25825: a pink pill holding the white
// bed icon, with a small downward tail.
const MapMarker = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  filter: drop-shadow(0 2px 6px rgba(10, 12, 13, 0.25));
`;

const MarkerPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimaryDefault};
`;

const MarkerTail = styled.span`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimaryDefault};
`;

// The Top Secret "somewhere in this area" circle, mirroring the interactive map's circle layer: a
// solid 2px ring with a 10% fill. It sits at the image's centre point, which for a Top Secret hotel
// is the offset area centre rather than the property (MPH-1048).
const AreaCircle = styled.div<{ $size: number; $widthRatio: number }>`
  position: absolute;
  left: 50%;
  top: 50%;
  /* $size is in the map image's own pixels, which are CSS pixels only while the element is no wider
     than the width asked of Mapbox. Past that, object-fit: cover scales the image up by
     renderedWidth / requestedWidth and the circle has to grow with it or it stops covering the 500 m
     it stands for. max() states that scale as a share of the element's own width, so it holds at any
     width with nothing to measure. */
  width: max(${({ $size }) => $size}px, ${({ $widthRatio }) => $widthRatio * 100}%);
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  border: 2px solid ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimaryDefault};
  border-radius: 50%;
  pointer-events: none;

  /* Fill on its own layer so the 10% opacity applies to it alone and the ring stays full strength,
     as it is on the interactive map (circle-opacity 0.1, no stroke-opacity). */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimaryDefault};
    opacity: 0.1;
  }
`;

const OpenMapPill = styled.span<{ $fullWidth: boolean }>`
  position: absolute;
  right: 16px;
  bottom: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  box-shadow:
    0 4px 8px rgba(10, 12, 13, 0.05),
    0 1px 3px rgba(10, 12, 13, 0.1);

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      left: 16px;
    `}
`;

// Our own Mapbox/OSM credit (the baked one is stripped via attribution=false). Mirrors the
// interactive map's attribution: small translucent chip, 12px. Sits bottom-left on desktop; on
// mobile the bottom is taken by the full-width button, so it moves to the top-left.
const Attribution = styled.div<{ $top: boolean }>`
  position: absolute;
  ${({ $top }) =>
    $top
      ? css`
          top: 8px;
          left: 8px;
        `
      : css`
          bottom: 10px;
          left: 4px;
        `}
  display: flex;
  gap: 4px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.6);
  ${({ theme }) => theme.kallisto.mixins.font('Caption')};

  a {
    color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const AddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// The hotel address line (pin icon + street). Placement (above/below the map) is up to the caller.
export function EvoLocationAddress() {
  const { data: staticData } = useFetchHotelStatic();

  if (!staticData?.direction) return null;

  return (
    <AddressRow>
      <Icon
        category="Location"
        name="icon-pin-point"
        size={16}
        footprint={20}
        color="iconSecondary"
        alt=""
        css="flex-shrink: 0;"
      />
      <Text variant="Body" color="textSecondary">
        {staticData.direction}
      </Text>
    </AddressRow>
  );
}

// Static Mapbox preview centred on the hotel, with a branded marker and an "Open map" control that
// opens the interactive map modal. Renders nothing without coordinates. A Top Secret property gets
// the area treatment instead of the marker — see the MPH-1048 note below.
export function EvoLocationMap({ width, height, borderRadius, fullWidthButton = false }: EvoLocationMapProps) {
  const { data: staticData } = useFetchHotelStatic();
  const { localization } = usePageWrapperData();
  const { isWhiteLabel } = localization;
  const { showMap } = useHotelMapModal();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { t } = useTranslation();

  const coordinates = staticData?.coordinates;
  if (!coordinates) return null;

  const openMap = () => {
    showMap();
    trackViewMap();
    sendCuriosityClickEvent({
      event_name: 'click_view_map_from_hotel_info',
      event_context: 'from_hotel_info',
    });
  };

  // MPH-1048: a Top Secret rate is sold on the customer not knowing which hotel it is, so the
  // preview shows the area the property is in rather than the property. Centring the image on the
  // hotel put the building dead centre of a street map, which identified it as plainly as a marker
  // would; it is centred on the offset area point instead, and framed so the whole circle fits.
  const areaCenter = staticData.isTopSecret ? tshAreaCenter(coordinates) : undefined;
  const areaFraming = areaCenter ? getTshPreviewFraming(areaCenter, height) : undefined;
  const mapUrl = buildHotelStaticMapUrl({
    coordinates: areaCenter ?? coordinates,
    width,
    height,
    isWhiteLabel,
    ...(areaFraming && { zoom: areaFraming.zoom }),
  });
  const openMapLabel = t('vacation.select.cc.hotel_info.open_map');

  return (
    <MapFigure>
      <MapButton
        type="button"
        $radius={borderRadius}
        onClick={openMap}
        aria-label={openMapLabel}
        data-testid="location-static-map"
      >
        <MapImg $height={height} src={mapUrl} alt="" loading="lazy" />
        {/* The marker sits on the map's centre point, which is the hotel's own coordinates — on a Top
            Secret property that pins the building on a street map and gives away the one thing the
            rate is sold on not telling you (MPH-935). The area circle takes its place, drawn around
            the offset centre the image is framed on. */}
        {areaFraming ? (
          <AreaCircle
            $size={areaFraming.diameterPx}
            $widthRatio={areaFraming.diameterPx / width}
            aria-hidden="true"
            data-testid="location-static-map-area"
          />
        ) : (
          <MapMarker aria-hidden="true">
            <MarkerPill>
              <Icon
                category="Services"
                name="icon-double-bed"
                size={20}
                color="iconOnDark"
                alt=""
                css="display: block;"
              />
            </MarkerPill>
            <MarkerTail />
          </MapMarker>
        )}
        <OpenMapPill $fullWidth={fullWidthButton}>
          <Text variant="Body" weight="medium">
            {openMapLabel}
          </Text>
        </OpenMapPill>
      </MapButton>
      <Attribution $top={fullWidthButton}>
        <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener noreferrer">
          © Mapbox
        </a>
        <a href="https://www.openstreetmap.org/about/" target="_blank" rel="noopener noreferrer">
          © OpenStreetMap
        </a>
      </Attribution>
    </MapFigure>
  );
}
